import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService, ImpersonateResponse } from '@absti/ng-lib';
import { AppStateService } from './app-state.service';
import { ShellStatusService } from '../proto/session/shell-status.service';
import { ProtoLookupService } from '../proto/shared/proto-lookup.service';
import {
  AuthResponse,
  LoginChallengeResponse,
  ResetPasswordChallenge,
  ResetPasswordConfirmBody,
  ResetPasswordRequestBody,
} from '../models/auth.interfaces';
import { AppConfig } from '../models/app-config.interfaces';

@Injectable({ providedIn: 'root' })
export class AppService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly appState = inject(AppStateService);
  private readonly shellStatus = inject(ShellStatusService);
  private readonly lookup = inject(ProtoLookupService);

  // ── App config ───────────────────────────────────────────────

  getConfig(): Observable<AppConfig> {
    return this.http.get<AppConfig>('/public/2/app/conf');
  }

  // ── Auth endpoints (/public/2/auth/*) ───────────────────────

  autoLogin(): Observable<void> {
    return this.http.post<AuthResponse>('/public/2/auth/refresh-token', {}).pipe(
      map(data => {
        if (data) this.applyAuth(data);
      }),
      catchError(() => of(undefined)),
    );
  }

  /**
   * A 202 (D-013) means the score triggered 2FA: no token, no cookie yet — the caller
   * must render the code step and call verifyLoginCode. A challenged login never calls setAuth.
   */
  login(
    username: string,
    password: string,
    keepLoggedIn: boolean,
    captcha: string | null,
  ): Observable<AuthResponse | LoginChallengeResponse> {
    return this.http
      .post<AuthResponse | LoginChallengeResponse>('/public/2/auth/login', {
        username,
        password,
        keepLoggedIn,
        captcha,
      })
      .pipe(
        map(data => {
          if ('mfaRequired' in data) {
            return data;
          }
          return this.applyAuth(data);
        }),
      );
  }

  verifyLoginCode(challengeId: string, code: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>('/public/2/auth/login/verify', { challengeId, code })
      .pipe(map(data => this.applyAuth(data)));
  }

  /** Stores the session the backend answered with: the library's auth state and the app's user details. */
  private applyAuth(data: AuthResponse): AuthResponse {
    this.authService.setAuth({
      token: data.token,
      expire: data.expire,
      user: data.user,
      roles: data.roles,
    });
    this.appState.usuario.set(data.details);
    this.shellStatus.reset();
    this.lookup.reset();
    return data;
  }

  /** The tree choice (P-03) and support mode change who the session is: same call shape as a login. */
  selectTree(treeOid: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/proto/session/tree', { treeOid }).pipe(map(data => this.applyAuth(data)));
  }

  enterSupport(treeOid: string, reason: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/proto/support/enter', { treeOid, reason }).pipe(map(data => this.applyAuth(data)));
  }

  exitSupport(): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/proto/support/exit', {}).pipe(map(data => this.applyAuth(data)));
  }

  logout(): Observable<void> {
    return this.http.post<void>('/public/2/auth/logout', {}).pipe(
      map(() => {
        this.authService.clearAuth();
        this.appState.usuario.set(null);
        this.shellStatus.reset();
        this.lookup.reset();
      }),
    );
  }

  requestPasswordReset(email: string, captcha: string | null): Observable<ResetPasswordChallenge> {
    const body: ResetPasswordRequestBody = { email, captcha };
    return this.http.post<ResetPasswordChallenge>('/public/2/app/reset-password/request', body);
  }

  confirmPasswordReset(challengeId: string, code: string, newPassword: string): Observable<void> {
    const body: ResetPasswordConfirmBody = { challengeId, code, newPassword };
    return this.http.post<void>('/public/2/app/reset-password/confirm', body);
  }

  // ── User endpoints (/api/2/*) ───────────────────────────────

  user(): Observable<any> {
    return this.http.get<any>('/api/2/users/me').pipe(
      map(data => {
        this.appState.usuario.set(data);
        return data;
      }),
    );
  }

  saveMisDatos(oid: string | number, data: any): Observable<any> {
    return this.http.put<any>(`/api/2/users/me/${oid}`, data);
  }

  changePassword(oid: string | number, old: string, nuevo: string): Observable<any> {
    return this.http.post<any>(`/api/2/users/me/${oid}/change-password`, { old, new: nuevo });
  }

  /**
   * Impersonation begins from the library's own security/users screen (D-040) — this app has
   * no impersonate-login call of its own. Ending it still needs to sync appState.usuario, which
   * AuthService.endImpersonation() has no reason to know about.
   */
  logoutImpersonated(): Observable<ImpersonateResponse> {
    return this.authService.endImpersonation().pipe(
      map(data => {
        this.appState.usuario.set(data.details);
        return data;
      }),
    );
  }
}
